import React from 'react';

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

/**
 * Normalize a stored symbol value into a canonical symbol id.
 * This lets users store things like "DT" but render "dt".
 */
export function normalizeSymbolId(raw) {
  const s = (raw ?? '').toString().trim();
  if (!s) return 'circle';
  const lower = s.toLowerCase();
  if (lower === 'dt' || lower === 'deciduous_tree' || lower === 'deciduoustree') return 'dt';
  return lower;
}

export const SYMBOL_OPTIONS = [
  { id: 'circle', label: 'Circle' },
  { id: 'ring', label: 'Ring' },
  { id: 'square', label: 'Square' },
  { id: 'triangle', label: 'Triangle' },
  { id: 'x', label: 'X' },
  { id: 'dot', label: 'Dot' },

  // Custom symbols
  { id: 'dt', label: 'Deciduous Tree (DT)' }
];

export function getSymbolViewBox(rawId, customSymbolsById) {
  const id = normalizeSymbolId(rawId);
  const custom = customSymbolsById && customSymbolsById[id];
  const vb = (custom && custom.svg_viewbox) ? String(custom.svg_viewbox) : null;
  return vb || '0 0 100 100';
}

function renderBuiltin(id) {
  switch (id) {
    case 'circle':
      return <circle cx="50" cy="50" r="42" fill="#16a34a" fillOpacity="0.85" stroke="#065f46" strokeWidth="4" />;
    case 'ring':
      return <circle cx="50" cy="50" r="42" fill="none" stroke="#16a34a" strokeWidth="10" />;
    case 'square':
      return <rect x="14" y="14" width="72" height="72" fill="#16a34a" fillOpacity="0.85" stroke="#065f46" strokeWidth="4" />;
    case 'triangle':
      return <polygon points="50,12 90,88 10,88" fill="#16a34a" fillOpacity="0.85" stroke="#065f46" strokeWidth="4" />;
    case 'x':
      return (
        <>
          <line x1="18" y1="18" x2="82" y2="82" stroke="#16a34a" strokeWidth="12" strokeLinecap="round" />
          <line x1="82" y1="18" x2="18" y2="82" stroke="#16a34a" strokeWidth="12" strokeLinecap="round" />
        </>
      );
    case 'dot':
      return <circle cx="50" cy="50" r="18" fill="#16a34a" />;
    default:
      return null;
  }
}

/**
 * First custom symbol: Deciduous Tree (DT)
 *
 * Screenshot-based recreation: pale green fill, dark outline,
 * scalloped edge detail, and a small center crosshair.
 */
function renderDeciduousTree() {
  const fill = '#dcebc3';
  const stroke = '#111827';
  const stroke2 = '#374151';

  // Scallop centers around the ring
  const scallopCenters = Array.from({ length: 8 }).map((_, i) => {
    const theta = (i * Math.PI) / 4; // 0..2π in 45° steps
    const r = 33.5;
    const cx = 50 + r * Math.cos(theta);
    const cy = 50 + r * Math.sin(theta);
    return { cx, cy };
  });

  return (
    <>
      <circle cx="50" cy="50" r="44" fill={fill} fillOpacity={clamp01(0.95)} stroke={stroke} strokeWidth="3" />
      {/* Scalloped edge detail */}
      {scallopCenters.map((c, idx) => (
        <circle key={idx} cx={c.cx} cy={c.cy} r="8.5" fill="none" stroke={stroke2} strokeWidth="2.2" opacity="0.9" />
      ))}
      {/* Center crosshair */}
      <line x1="50" y1="42" x2="50" y2="58" stroke={stroke2} strokeWidth="2" strokeLinecap="round" />
      <line x1="42" y1="50" x2="58" y2="50" stroke={stroke2} strokeWidth="2" strokeLinecap="round" />
    </>
  );
}

export function renderSymbolSvgChildren(rawId, opts = {}) {
  const id = normalizeSymbolId(rawId);
  const custom = opts?.customSymbolsById && opts.customSymbolsById[id];
  if (custom?.svg_body) {
    return <g dangerouslySetInnerHTML={{ __html: String(custom.svg_body) }} />;
  }
  if (id === 'dt') return renderDeciduousTree();
  return renderBuiltin(id) || renderBuiltin('circle');
}

